import { supabase } from "@/integrations/supabase/client";
import { edgeInvoke } from "@/lib/edgeInvoke";

export interface VideoFrame {
  url: string;
  timestamp: number;
  index: number;
  compressed?: boolean;
  originalSize?: number;
  compressedSize?: number;
}

export interface FrameExtractionConfig {
  strategy: 'all' | 'fps' | 'keyframe';
  targetFps?: number;
  maxFrames?: number;
  compressionQuality?: number;
  maxWidth?: number;
  maxHeight?: number;
}

export interface FrameExtractionResult {
  frames: VideoFrame[];
  framesDecoded: number;
  framesCaptured: number;
  compressionApplied: boolean;
  totalOriginalSize: number;
  totalCompressedSize: number;
}

// S-2210: Enhanced frame extraction with full capture capability
export async function extractAllVideoFrames(
  playbackId: string, 
  config: FrameExtractionConfig = { strategy: 'all' },
  duration?: number
): Promise<FrameExtractionResult> {
  try {
    console.log(`[S-2210] Extracting ALL frames from playback ID: ${playbackId}, strategy: ${config.strategy}`);
    
    // Validate inputs
    if (!playbackId || typeof playbackId !== 'string') {
      throw new Error('Invalid playback ID provided');
    }
    
    // Get video info
    const { data: video, error } = await supabase
      .from('videos')
      .select('playback_policy, duration')
      .eq('mux_playback_id', playbackId)
      .single();

    if (error) {
      console.warn('Could not fetch video metadata, using defaults:', error);
    }

    const playbackPolicy = video?.playback_policy || 'signed';
    const videoDuration = duration || video?.duration || 14;
    
    console.log(`[S-2210] Video duration: ${videoDuration}s, policy: ${playbackPolicy}`);
    
    // Calculate frame extraction strategy
    const { frameTimestamps, expectedFrameCount } = calculateFrameStrategy(videoDuration, config);
    
    console.log(`[S-2210] Strategy: ${config.strategy}, expected frames: ${expectedFrameCount}, actual timestamps: ${frameTimestamps.length}`);
    
    const frames: VideoFrame[] = [];
    let framesDecoded = frameTimestamps.length;
    let framesCaptured = 0;
    let totalOriginalSize = 0;
    let totalCompressedSize = 0;
    
    // Extract all frames
    for (let i = 0; i < frameTimestamps.length; i++) {
      const timestamp = frameTimestamps[i];
      
      try {
        if (playbackPolicy !== 'signed') {
          throw new Error(`All videos must use signed playback policy, got: ${playbackPolicy}`);
        }
        
        const result = await edgeInvoke('playback-url', { 
          playback_id: playbackId,
          type: 'thumbnail',
          time: timestamp
        });
        
        if (result?.url) {
          // Estimate frame size (actual compression would happen in payload builder)
          const estimatedSize = 50000; // ~50KB per frame estimate
          const compressedSize = config.compressionQuality ? estimatedSize * config.compressionQuality : estimatedSize;
          
          frames.push({
            url: result.url,
            timestamp: timestamp,
            index: i,
            compressed: !!config.compressionQuality,
            originalSize: estimatedSize,
            compressedSize: compressedSize
          });
          
          framesCaptured++;
          totalOriginalSize += estimatedSize;
          totalCompressedSize += compressedSize;
          
          if (import.meta.env.VITE_DEBUG_FRAMES === 'true') {
            console.log(`[S-2210] Frame ${i}: t=${timestamp}s, size=${compressedSize}B`);
          }
        } else {
          console.error(`[S-2210] Failed to get signed URL for timestamp ${timestamp}`);
          framesDecoded--; // Decode error, don't count as decoded
        }
      } catch (frameError) {
        console.error(`[S-2210] Frame extraction error at ${timestamp}s:`, frameError);
        framesDecoded--; // Decode error
      }
    }
    
    console.log(`[S-2210] Frame audit: decoded=${framesDecoded}, captured=${framesCaptured}, compression=${!!config.compressionQuality}`);
    
    if (framesCaptured === 0) {
      throw new Error('No frames were captured from the video');
    }
    
    return {
      frames,
      framesDecoded,
      framesCaptured,
      compressionApplied: !!config.compressionQuality,
      totalOriginalSize,
      totalCompressedSize
    };
  } catch (error) {
    console.error('[S-2210] Error in extractAllVideoFrames:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    throw new Error(`Failed to extract video frames: ${errorMessage}`);
  }
}

function calculateFrameStrategy(duration: number, config: FrameExtractionConfig): { frameTimestamps: number[], expectedFrameCount: number } {
  const startOffset = duration * 0.05; // Start 5% into video
  const endOffset = duration * 0.95;   // End at 95% of video
  const effectiveDuration = endOffset - startOffset;
  
  let frameTimestamps: number[] = [];
  let expectedFrameCount = 0;
  
  switch (config.strategy) {
    case 'all': {
      // Capture at 30fps for full coverage
      const targetFps = 30;
      const frameInterval = 1 / targetFps;
      expectedFrameCount = Math.floor(effectiveDuration * targetFps);
      
      for (let i = 0; i < expectedFrameCount; i++) {
        frameTimestamps.push(startOffset + (i * frameInterval));
      }
      break;
    }
      
    case 'fps': {
      const fps = config.targetFps || 10;
      const interval = 1 / fps;
      expectedFrameCount = Math.floor(effectiveDuration * fps);
      
      for (let i = 0; i < expectedFrameCount; i++) {
        frameTimestamps.push(startOffset + (i * interval));
      }
      break;
    }
      
    case 'keyframe': {
      // Simulate keyframe detection with regular intervals
      const keyframeInterval = 2; // Every 2 seconds
      expectedFrameCount = Math.floor(effectiveDuration / keyframeInterval);
      
      for (let i = 0; i < expectedFrameCount; i++) {
        frameTimestamps.push(startOffset + (i * keyframeInterval));
      }
      break;
    }
  }
  
  // Apply max frames limit if specified
  if (config.maxFrames && frameTimestamps.length > config.maxFrames) {
    const step = frameTimestamps.length / config.maxFrames;
    const limitedTimestamps: number[] = [];
    
    for (let i = 0; i < config.maxFrames; i++) {
      const index = Math.floor(i * step);
      limitedTimestamps.push(frameTimestamps[index]);
    }
    
    frameTimestamps = limitedTimestamps;
    expectedFrameCount = config.maxFrames;
  }
  
  return { frameTimestamps, expectedFrameCount };
}

// Legacy function for backward compatibility
export async function extractVideoFrames(playbackId: string, frameCount: number = 10, duration?: number): Promise<VideoFrame[]> {
  try {
    console.log(`Extracting ${frameCount} frames from playback ID: ${playbackId}`);
    
    // S-2202: Validate inputs
    if (!playbackId || typeof playbackId !== 'string') {
      throw new Error('Invalid playback ID provided');
    }
    
    if (frameCount < 1 || frameCount > 50) {
      throw new Error('Frame count must be between 1 and 50');
    }
    
    // Get video info to check playback policy and duration
    const { data: video, error } = await supabase
      .from('videos')
      .select('playback_policy, duration')
      .eq('mux_playback_id', playbackId)
      .single();

    if (error) {
      console.warn('Could not fetch video metadata, using defaults:', error);
    }

    const playbackPolicy = video?.playback_policy || 'signed'; // Default to signed
    const videoDuration = duration || video?.duration || 14; // Use provided, video duration, or default to 14 seconds
    
    console.log(`Video duration: ${videoDuration}s, playback policy: ${playbackPolicy}`);
    
    // Generate frame URLs at different timestamps
    const frames: VideoFrame[] = [];
    
    // Extract frames at regular intervals, skipping the very first and last frames to avoid static shots
    const startOffset = videoDuration * 0.1; // Start 10% into the video
    const endOffset = videoDuration * 0.9; // End at 90% of the video
    const effectiveDuration = endOffset - startOffset;
    const interval = effectiveDuration / (frameCount - 1);
    
    console.log(`Frame extraction: start=${startOffset}s, end=${endOffset}s, interval=${interval}s`);
    
    for (let i = 0; i < frameCount; i++) {
      const timestamp = startOffset + (i * interval);
      
      let frameUrl: string;
      
      if (playbackPolicy === 'signed') {
        // Use signed URLs for protected content with timestamp included in signature
        if (import.meta.env.VITE_DEBUG_THUMBS === 'true') {
          console.log(`Getting signed URL for timestamp: ${timestamp}s`);
        }
        const result = await edgeInvoke('playback-url', { 
          playback_id: playbackId,
          type: 'thumbnail',
          time: timestamp
        });
        
        if (result?.url) {
          frameUrl = result.url;
          if (import.meta.env.VITE_DEBUG_THUMBS === 'true') {
            console.log(`Got signed URL for frame ${i}: ${frameUrl.substring(0, 100)}...`);
          }
        } else {
          console.error(`Failed to get signed URL for timestamp ${timestamp}:`, result);
          // S-2202: Provide more specific error message
          throw new Error(`Failed to generate signed frame URL for timestamp ${timestamp}. Edge function may be unavailable.`);
        }
      } else {
        // SECURITY: All videos must be signed - no public URLs allowed
        console.error('Invalid playback policy for frame extraction - only signed videos allowed');
        throw new Error(`All videos must use signed playback policy, got: ${playbackPolicy}`);
      }
      
      frames.push({
        url: frameUrl,
        timestamp: timestamp
      });
    }
    
    console.log(`Successfully extracted ${frames.length} frames`);
    
    // S-2202: Validate frame extraction success
    if (frames.length === 0) {
      throw new Error('No frames were extracted from the video');
    }
    
    if (frames.length < frameCount * 0.5) {
      console.warn(`Only extracted ${frames.length} frames out of requested ${frameCount}`);
    }
    
    // Validate that we have unique frames (different timestamps)
    const uniqueTimestamps = new Set(frames.map(f => f.timestamp));
    if (uniqueTimestamps.size < frames.length * 0.8) {
      console.warn('Warning: Many frames have duplicate timestamps, this may affect analysis quality');
    }
    
    // S-2202: Ensure minimum frame count for analysis
    if (frames.length < 5) {
      throw new Error(`Insufficient frames extracted (${frames.length}). Need at least 5 frames for analysis.`);
    }
    
    return frames;
  } catch (error) {
    console.error('Error extracting video frames:', error);
    // S-2202: Preserve original error message for better debugging
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    throw new Error(`Failed to extract video frames: ${errorMessage}`);
  }
}

export async function getVideoPlaybackId(videoId: string): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('videos')
      .select('mux_playback_id')
      .eq('id', videoId)
      .single();

    if (error) {
      console.error('Error fetching video playback ID:', error);
      return null;
    }

    return data?.mux_playback_id || null;
  } catch (error) {
    console.error('Error in getVideoPlaybackId:', error);
    return null;
  }
}